import { ColDef, GridApi } from '@ag-grid-community/core';
import flatMap from 'lodash-es/flatMap';
import isObject from 'lodash-es/isObject';

import {
  ColumnChooserModel,
  ColumnChooserNode,
} from '../components/column-chooser-modal/model/column-chooser.model';

export class ColumnChooserService {
  private readonly gridApi!: GridApi;

  applyColumnChooser(configLsKey: string, reorder = false, excludeColumns: string[] = []): boolean {
    const columns = this.getColumnChooserConfig(configLsKey);

    try {
      const validated = this.validateConfigStructure(columns);

      if (!validated) {
        throw new Error('Invalid column chooser config structure');
      }

      const filteredColumns = this.excludeColumnsFromResultConfig(columns, excludeColumns);

      this.applyColumnsVisibility(filteredColumns);

      if (reorder) this.applyReorders(filteredColumns);

      return true;
    } catch (error) {
      console.error('Error applying column chooser', error);

      return false;
    }
  }

  private applyColumnsVisibility(columns: ColumnChooserNode[]) {
    columns.forEach((column) => {
      if (column.ignoreChildrenSelection && ColumnChooserModel.isColumnGroup(column)) {
        const colDef = this.gridApi.getColumnDefs()?.find((col) => {
          return ColumnChooserModel.isColumnGroup(col) ? col.groupId === column.field : false;
        });

        if (colDef && 'children' in colDef) {
          column.children = colDef.children.map((child) => ({
            checked: column.checked,
            children: [],
            field: (<ColDef>child).field as string,
            headerName: (<ColDef>child).headerName as string,
            pinned: (<ColDef>child).pinned === 'left',
          }));
        }
      }

      if (column.children?.length) {
        this.applyColumnsVisibility(column.children);
      }

      if (column.field) {
        // Only pin if visible (checked)
        const shouldPin = !!column.checked && column.pinned;

        this.gridApi.setColumnPinned(column.field, shouldPin ? 'left' : null);
        this.gridApi.setColumnVisible(column.field, !!column.checked);
      }
    });
  }

  private applyReorders(columns: ColumnChooserNode[]) {
    const groupedColumns = (this.gridApi.getColumnDefs() || []).filter((col) =>
      'rowGroup' in col ? col.rowGroup : false
    );

    const lastGroupedColumnIndex =
      (groupedColumns[groupedColumns.length - 1] as ColDef)?.rowGroupIndex || 0;

    const flatColumns = flatMap(columns, (column) => {
      if (column.children?.length) {
        return column.children;
      }

      return column;
    });

    flatColumns.forEach((column, index) => {
      if (column.field) {
        this.gridApi.moveColumns([column.field], index + lastGroupedColumnIndex);
      }
    });
  }

  static atLeastOneColumnSelected(columns: ColumnChooserNode[]): boolean {
    const hasCheckedColumn = columns.some((column) => {
      if (column.children?.length) {
        return this.atLeastOneColumnSelected(column.children);
      }

      return !!column.checked;
    });

    return hasCheckedColumn;
  }

  constructor(api: GridApi) {
    this.gridApi = api;
  }

  excludeColumnsFromResultConfig(
    config: ColumnChooserNode[],
    excludeColumns: string[]
  ): ColumnChooserNode[] {
    return config.filter((column) => {
      // Filter out columns that are in the exclude list
      if (excludeColumns.includes(column.field)) {
        return false;
      }

      // If the column has children, recursively filter them too
      if (column.children?.length && !column.ignoreChildrenSelection) {
        column.children = this.excludeColumnsFromResultConfig(column.children, excludeColumns);
        column.checked = ColumnChooserModel.areAllChildrenChecked(column.children);
      }

      return true;
    });
  }

  private flattenConfig(config: ColumnChooserNode[], map: Map<string, ColumnChooserNode>) {
    config.forEach((node) => {
      if (node.field) {
        map.set(node.field, node);
      }

      if (node.children?.length) {
        this.flattenConfig(node.children, map);
      }
    });
  }

  getColumnChooserConfig(configLsKey: string): unknown {
    try {
      const config = localStorage.getItem(configLsKey);

      return config ? JSON.parse(config) : [];
    } catch (error) {
      console.error('Error getting column chooser config', error);

      return [];
    }
  }

  getPinnedColumnCount(config: ColumnChooserNode[]): number {
    let count = 0;

    for (const column of config) {
      if (column.pinned && column.checked) {
        count++;
      }

      if (column.children?.length) {
        count += this.getPinnedColumnCount(column.children);
      }
    }

    return count;
  }

  getTypeSafeColumnChooserConfig(
    configLsKey: string,
    excludeColumns: string[]
  ): ColumnChooserNode[] {
    const config = this.getColumnChooserConfig(configLsKey);

    if (!this.validateConfigStructure(config)) {
      throw new Error('Invalid column chooser config structure');
    }

    const filteredConfig = this.excludeColumnsFromResultConfig(config, excludeColumns);

    return filteredConfig || [];
  }

  hasColumnChooserConfig(configLsKey: string): boolean {
    const config = this.getColumnChooserConfig(configLsKey);

    return Array.isArray(config) && config.length > 0 && this.validateConfigStructure(config);
  }

  private mergeConfigRecursive(
    currentConfig: ColumnChooserNode[],
    savedConfigMap: Map<string, ColumnChooserNode>,
    excludeColumns: string[]
  ): ColumnChooserNode[] {
    return currentConfig
      .filter((node) => !excludeColumns.includes(node.field))
      .map((node) => {
        const savedNode = savedConfigMap.get(node.field);
        const mergedNode: ColumnChooserNode = {
          ...node,
          checked: savedNode?.checked ?? node.checked,
          isCollapsed: savedNode?.isCollapsed ?? node.isCollapsed,
          pinned: savedNode?.pinned ?? node.pinned,
        };

        if (node.children?.length) {
          // For groups with ignoreChildrenSelection, preserve the original structure
          if (node.ignoreChildrenSelection) {
            // Keep the children but don't update parent state based on them
            mergedNode.children = this.mergeConfigRecursive(
              node.children,
              savedConfigMap,
              excludeColumns
            );
            // Preserve the original checked state from saved config or current config
            mergedNode.checked = savedNode?.checked ?? node.checked;
            mergedNode.indeterminate = savedNode?.indeterminate ?? node.indeterminate;
          } else {
            // For normal groups, update parent state based on children
            mergedNode.children = this.mergeConfigRecursive(
              node.children,
              savedConfigMap,
              excludeColumns
            );

            const allChecked = ColumnChooserModel.areAllChildrenChecked(mergedNode.children);
            const someChecked = ColumnChooserModel.areSomeChildrenChecked(mergedNode.children);

            mergedNode.checked = allChecked;
            mergedNode.indeterminate = !allChecked && someChecked;
          }
        }

        return mergedNode;
      });
  }

  /**
   * Merges a saved configuration with the current grid configuration.
   * This ensures that user preferences are preserved while handling dynamic columns
   * that may appear/disappear based on vendor selection or other conditions.
   */
  mergeConfigWithCurrentGrid(
    savedConfig: ColumnChooserNode[],
    excludeColumns: string[] = []
  ): ColumnChooserNode[] {
    const currentColumnDefs = this.gridApi.getColumnDefs() || [];
    const currentModel = new ColumnChooserModel(currentColumnDefs);
    const currentConfig = currentModel.columns;

    // Create a map of saved configurations for quick lookup
    const savedConfigMap = new Map<string, ColumnChooserNode>();

    this.flattenConfig(savedConfig, savedConfigMap);

    // Merge current config with saved preferences
    const mergedConfig = this.mergeConfigRecursive(currentConfig, savedConfigMap, excludeColumns);

    return mergedConfig;
  }

  saveColumnChooser(columns: ColumnChooserNode[], configLsKey: string) {
    localStorage.setItem(configLsKey, JSON.stringify(columns));
  }

  validateConfigStructure(columns: unknown): columns is ColumnChooserNode[] {
    if (!Array.isArray(columns)) {
      return false;
    }

    const requiredKeys: (keyof ColumnChooserNode)[] = ['field', 'headerName', 'checked'];

    return columns.every((column) => {
      return isObject(column) && requiredKeys.every((key) => key in column);
    });
  }
}
